import React from "react";
import { commitMutation, graphql } from "react-relay";

import { faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { compact } from "lodash";
import uuidv4 from "uuid/v4";

import EmailListInput from "~/common/EmailListInput";
import UserMultiSelector, {
	defaultLoadOptions,
	type MultiValueType,
	type UserMultiSelectorOption,
} from "~/common/UserSelect/UserMultiSelector";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { listFormat } from "~/lib/utils";
import { environment } from "~/models";
import { Button, toast } from "~/ui";
import { HelpCenterIcon, Modal } from "~/ui/legacyFellowIndex";

import type { AddDirectReportModalMutation as MutationType } from "~/__generated__/AddDirectReportModalMutation.graphql";

import styles from "./css/Modals.module.css";

const AddReportsMutation = graphql`
	mutation AddDirectReportModalMutation($reportEmails: [String!]) {
		addDirectReports(reportEmails: $reportEmails) {
			newReports
			failedReports
			user {
				internalAttributes {
					directReports {
						edges {
							node {
								...DirectReports_reportInfo
							}
						}
					}
				}
			}
		}
	}
`;

interface Props {
	onClose: () => void;
	refetch: () => void;
	userId: string;
	excludedEmails: string[];
	excludedUserIds: string[];
	internalInvitesAllowed: boolean;
	guestInvitesAllowed: boolean;
}

type ReportSelection = { email: string } | { user: { email: string } | null };

export function AddDirectReportModal({
	onClose,
	refetch,
	userId,
	excludedEmails,
	excludedUserIds,
	internalInvitesAllowed,
	guestInvitesAllowed,
}: Props) {
	const [reportEmails, setReportEmails] = React.useState<string[]>();
	const [selectedReports, setSelectedReports] = React.useState<readonly UserMultiSelectorOption[]>([]);
	const [defaultUsers, setDefaultUsers] = React.useState<readonly UserMultiSelectorOption[] | undefined>(undefined);
	const [isLoading, setIsLoading] = React.useState(false);

	const analyticsProperties = React.useMemo(
		() => ({
			fingerprint: uuidv4(),
		}),
		[],
	);

	React.useEffect(() => {
		dispatchAnalyticsTrack("[Add Direct Report Modal] Opened Modal", analyticsProperties);
		if (internalInvitesAllowed) return;
		defaultLoadOptions({
			excludeUserIDs: excludedUserIds,
			ranked: true,
			excludeManagedUsers: true,
			excludeAncestryFor: userId,
		}).then(data => {
			if (!data) return;

			const { viewer } = data;

			const options =
				viewer.selectUsers?.edges.map(user => {
					return {
						value: user.node.id,
						label: user.node.fullName,
						email: user.node.email,
						avatar: {
							id: user.node.id,
							fullName: user.node.fullName,
							isGuest: user.node.isGuest,
							image: user.node.image,
						},
					};
				}) ?? [];

			setDefaultUsers(options as UserMultiSelectorOption[]);
		});
	}, [internalInvitesAllowed, guestInvitesAllowed, excludedUserIds, userId, analyticsProperties]);

	const onDismiss = React.useCallback(() => {
		dispatchAnalyticsTrack("[Add Direct Report Modal] Dismissed Modal", analyticsProperties);
		onClose();
	}, [analyticsProperties, onClose]);

	const onReportsChange = React.useCallback((val: readonly ReportSelection[] | ReportSelection | null | undefined) => {
		const newUsers = val ? ("length" in val ? val : [val]) : [];
		const emails = compact(newUsers.map(report => ("user" in report ? report.user?.email : report.email)));
		setReportEmails(emails);
	}, []);

	const onReportChangeUserSelector = React.useCallback((reports: MultiValueType<UserMultiSelectorOption>) => {
		const emails = compact(reports.map(report => report.email));
		setSelectedReports(reports);
		setReportEmails(emails);
	}, []);

	const onAdd = React.useCallback(() => {
		setIsLoading(true);
		commitMutation<MutationType>(environment, {
			mutation: AddReportsMutation,
			variables: {
				reportEmails,
			},
			onCompleted: ({ addDirectReports }, errors) => {
				setIsLoading(false);

				if (errors?.length) {
					errors.forEach(error => toast.error(error.message));
					return;
				}

				if (!addDirectReports) {
					toast.error("Something went wrong; please contact support.");
					throw new Error(`Failed to receive new reports: ${addDirectReports}`);
				}

				refetch();
				onClose();

				const { newReports, failedReports } = addDirectReports;

				let failedMessage = "";
				let successMessage = "";

				if (failedReports.length) {
					dispatchAnalyticsTrack("[Add Direct Report Modal] Failed to Add Direct Reports", {
						...analyticsProperties,
						numReports: failedReports.length,
					});
					failedMessage = `Failed to add ${listFormat(failedReports).join("")} to your direct reports.`;
					toast.error(failedMessage, {
						title: "Error",
					});
				}

				if (newReports.length) {
					dispatchAnalyticsTrack("[Add Direct Report Modal] Successfully Added Direct Reports", {
						...analyticsProperties,
						numReports: newReports.length,
					});
					successMessage =
						newReports.length > 1
							? `${listFormat(newReports).join("")} have been added to your direct reports`
							: `${newReports[0]} has been added to your direct reports`;

					toast.success(successMessage, {
						title: "Direct report successfully added",
					});
				}
			},
		});
	}, [reportEmails, analyticsProperties, refetch, onClose]);

	return (
		<Modal onClose={onDismiss} className={styles.modal}>
			<Modal.Header className={styles.header}>
				<Modal.Title>Add direct reports</Modal.Title>
				<Modal.HeaderActions onClose={onDismiss}>
					<HelpCenterIcon articleId="6739050" className={styles.helpLink} title="Managing your direct reports" />
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body>
				<p className={styles.bodyText}>
					You can only add a user as a direct report if they do not have a manager set already.
				</p>
				<h6 className={styles.selectHeading}>Direct reports</h6>
				{internalInvitesAllowed ? (
					<>
						<EmailListInput
							isMulti
							useNewTeammateBtn
							excludeManagedUsers
							internalInvitesAllowed={internalInvitesAllowed}
							externalInvitesAllowed={false}
							guestInvitesAllowed={guestInvitesAllowed}
							excludeAncestryFor={userId}
							exclude={excludedEmails}
							placeholder="Add one or multiple reports by name or email"
							onChange={onReportsChange}
						/>
						<small className={styles.subText}>New teammates will be invited to join Fellow.</small>
					</>
				) : (
					<UserMultiSelector
						excludeManagedUsers
						isLoading={defaultUsers === undefined}
						defaultUsers={defaultUsers}
						selectedUsers={selectedReports}
						excludeUserIDs={excludedUserIds}
						excludeAncestryFor={userId}
						placeholder="Add one or multiple reports"
						onChange={onReportChangeUserSelector}
					/>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={onDismiss}>
					Cancel
				</Button>
				<Button variant="primary" disabled={!reportEmails || isLoading} onClick={onAdd}>
					Add {isLoading && <FontAwesomeIcon icon={faSpinnerThird} spin />}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
